import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { CheckCircleIcon, ClockIcon, ArrowPathIcon, XCircleIcon, CameraIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';

// Step 1: Name, Email, Phone + OTP Verification
const Step1OTPVerification: React.FC<{
  onVerified: (userData: { first_name: string; last_name: string; email: string; phone: string }) => void;
}> = ({ onVerified }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expiryTime, setExpiryTime] = useState<Date | null>(null);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const [canResend, setCanResend] = useState(false);
  const otpInputs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer for OTP expiry
  useEffect(() => {
    if (!otpSent || !expiryTime) return;

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const expiry = new Date(expiryTime).getTime();
      const difference = Math.floor((expiry - now) / 1000);

      if (difference <= 0) {
        setTimeLeft(0);
        setCanResend(true);
        clearInterval(timer);
      } else {
        setTimeLeft(difference);
        setCanResend(difference <= 270); // Allow resend after 30 seconds
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [otpSent, expiryTime]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/registration/send-otp', formData);
      setOtpSent(true);
      setExpiryTime(new Date(response.data.expiry_time));
      setSuccess('OTP sent successfully! Please check your email and phone.');
      // Auto-focus first OTP input
      setTimeout(() => otpInputs.current[0]?.focus(), 100);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpInputs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter a complete 6-digit OTP');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await api.post('/auth/registration/verify-otp', {
        email: formData.email,
        otp_code: otpCode
      });
      
      setSuccess('OTP verified successfully! Proceeding to complete registration...');
      setTimeout(() => {
        onVerified(formData);
      }, 1000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid OTP. Please try again.');
      setOtp(['', '', '', '', '', '']);
      otpInputs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setError('');
    setLoading(true);

    try {
      const response = await api.post(`/auth/registration/resend-otp?email=${formData.email}`);
      setExpiryTime(new Date(response.data.expiry_time));
      setOtp(['', '', '', '', '', '']);
      setSuccess('New OTP sent successfully!');
      setCanResend(false);
      otpInputs.current[0]?.focus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!otpSent) {
    return (
      <div className="bg-white p-8 rounded-volexx-lg shadow-volexx-soft border border-gray-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-2 font-poppins">Dispatcher Registration</h3>
        <p className="text-gray-600 mb-6 font-poppins">Step 1: Verify your contact information</p>
        
        <form onSubmit={handleSendOTP} className="space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-volexx text-sm font-poppins flex items-center">
              <XCircleIcon className="h-5 w-5 mr-2" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="first_name" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins transition-all"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="Enter first name"
              />
            </div>

            <div>
              <label htmlFor="last_name" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins transition-all"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins transition-all"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins transition-all"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1234567890"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-volexx-orange-500 hover:bg-volexx-orange-600 active:bg-volexx-orange-700 text-white font-bold py-3 px-6 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-orange-500 focus:ring-offset-2 font-poppins transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Sending OTP...' : 'Send Verification Code'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-volexx-lg shadow-volexx-soft border border-gray-200">
      <h3 className="text-2xl font-bold text-gray-900 mb-2 font-poppins">Verify Your Identity</h3>
      <p className="text-gray-600 mb-6 font-poppins">
        We've sent a 6-digit code to <span className="font-semibold">{formData.email}</span> and <span className="font-semibold">{formData.phone}</span>
      </p>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-volexx text-sm font-poppins flex items-center mb-5">
          <CheckCircleIcon className="h-5 w-5 mr-2" />
          {success}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-volexx text-sm font-poppins flex items-center mb-5">
          <XCircleIcon className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3 font-poppins text-center">
          Enter 6-Digit Verification Code
        </label>
        <div className="flex justify-center gap-3">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { otpInputs.current[index] = el; }}
              type="text"
              maxLength={1}
              className="w-14 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins transition-all"
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(index, e)}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center mb-6 text-sm font-poppins">
        <ClockIcon className="h-5 w-5 text-gray-500 mr-2" />
        <span className={`font-semibold ${timeLeft < 60 ? 'text-red-600' : 'text-gray-600'}`}>
          {timeLeft > 0 ? `Code expires in ${formatTime(timeLeft)}` : 'Code expired'}
        </span>
      </div>

      <button
        onClick={handleVerifyOTP}
        disabled={loading || otp.join('').length !== 6}
        className="w-full bg-volexx-orange-500 hover:bg-volexx-orange-600 active:bg-volexx-orange-700 text-white font-bold py-3 px-6 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-orange-500 focus:ring-offset-2 font-poppins transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-3"
      >
        {loading ? 'Verifying...' : 'Verify Code'}
      </button>

      <button
        onClick={handleResendOTP}
        disabled={!canResend || loading}
        className="w-full bg-white border-2 border-volexx-blue-500 text-volexx-blue-500 hover:bg-blue-50 active:bg-blue-100 font-bold py-3 px-6 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:ring-offset-2 font-poppins transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        <ArrowPathIcon className="h-5 w-5 mr-2" />
        {canResend ? 'Resend Code' : `Resend in ${formatTime(timeLeft)}`}
      </button>
    </div>
  );
};

// Bank interface
interface Bank {
  name: string;
  code: string;
  slug?: string;
}

// Step 2: Complete Registration (Vehicle, Bank Details, etc.)
const Step2CompleteRegistration: React.FC<{
  userData: { first_name: string; last_name: string; email: string; phone: string };
}> = ({ userData }) => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    vehicle_type: 'motorbike',
    license_plate: '',
    capacity_kg: '',
    make: '',
    model: '',
    year: '',
    color: '',
    bank_code: '',
    account_number: '',
    account_name: '',
  });
  const [banks, setBanks] = useState<Bank[]>([]);
  const [banksLoading, setBanksLoading] = useState(true);
  const [bankSearch, setBankSearch] = useState('');
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [verifyingAccount, setVerifyingAccount] = useState(false);
  const [riderPhoto, setRiderPhoto] = useState<File | null>(null);
  const [riderPhotoPreview, setRiderPhotoPreview] = useState<string | null>(null);
  const [vehiclePhoto, setVehiclePhoto] = useState<File | null>(null);
  const [vehiclePhotoPreview, setVehiclePhotoPreview] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const riderPhotoRef = useRef<HTMLInputElement>(null);
  const riderCameraRef = useRef<HTMLInputElement>(null);
  const vehiclePhotoRef = useRef<HTMLInputElement>(null);
  const vehicleCameraRef = useRef<HTMLInputElement>(null);
  const bankDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Fetch banks on component mount
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await api.get('/banks');
        setBanks(response.data.banks || []);
      } catch (err) {
        console.error('Failed to fetch banks:', err);
      } finally {
        setBanksLoading(false);
      }
    };
    fetchBanks();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bankDropdownRef.current && !bankDropdownRef.current.contains(event.target as Node)) {
        setShowBankDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter banks based on search
  const filteredBanks = banks.filter(bank => 
    bank.name.toLowerCase().includes(bankSearch.toLowerCase())
  );

  // Handle bank selection
  const handleBankSelect = (bank: Bank) => {
    setSelectedBank(bank);
    setFormData({ ...formData, bank_code: bank.code });
    setBankSearch(bank.name);
    setShowBankDropdown(false);
    
    // Auto-verify account if account number is already entered
    if (formData.account_number.length === 10) {
      verifyBankAccount(formData.account_number, bank.code);
    }
  };

  // Verify bank account
  const verifyBankAccount = async (accountNumber: string, bankCode: string) => {
    if (accountNumber.length !== 10 || !bankCode) return;
    
    setVerifyingAccount(true);
    try {
      const response = await api.post('/banks/resolve', null, {
        params: { account_number: accountNumber, bank_code: bankCode }
      });
      if (response.data.account_name) {
        setFormData(prev => ({ ...prev, account_name: response.data.account_name }));
      }
    } catch (err) {
      console.error('Account verification failed:', err);
    } finally {
      setVerifyingAccount(false);
    }
  };

  // Handle account number change with auto-verification
  const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, account_number: value });
    
    if (value.length === 10 && selectedBank) {
      verifyBankAccount(value, selectedBank.code);
    }
  };

  // Determine capacity unit based on vehicle type
  const getCapacityUnit = () => {
    const heavyVehicles = ['12_wheeler', '18_wheeler', 'pickup_truck'];
    return heavyVehicles.includes(formData.vehicle_type) ? 'tonnes' : 'kg';
  };

  // Check if license plate is required for this vehicle type
  const isLicensePlateRequired = () => {
    return formData.vehicle_type !== 'bicycle';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'rider' | 'vehicle') => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'rider') {
          setRiderPhoto(file);
          setRiderPhotoPreview(reader.result as string);
        } else {
          setVehiclePhoto(file);
          setVehiclePhotoPreview(reader.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const clearPhoto = (type: 'rider' | 'vehicle') => {
    if (type === 'rider') {
      setRiderPhoto(null);
      setRiderPhotoPreview(null);
      if (riderPhotoRef.current) riderPhotoRef.current.value = '';
      if (riderCameraRef.current) riderCameraRef.current.value = '';
    } else {
      setVehiclePhoto(null);
      setVehiclePhotoPreview(null);
      if (vehiclePhotoRef.current) vehiclePhotoRef.current.value = '';
      if (vehicleCameraRef.current) vehicleCameraRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!formData.capacity_kg) {
      setError(`Please provide vehicle capacity in ${getCapacityUnit()}`);
      return;
    }

    // Validate license plate only for non-bicycle vehicles
    if (isLicensePlateRequired() && !formData.license_plate) {
      setError('Please provide license plate for this vehicle type');
      return;
    }

    if (!formData.make || !formData.color) {
      setError('Please provide vehicle make and color');
      return;
    }

    if (!selectedBank || !formData.bank_code) {
      setError('Please select your bank');
      return;
    }

    if (!formData.account_number || formData.account_number.length !== 10) {
      setError('Please provide a valid 10-digit account number');
      return;
    }

    if (!formData.account_name) {
      setError('Please provide your account name');
      return;
    }

    if (!riderPhoto) {
      setError('Please upload your photo');
      return;
    }

    if (!vehiclePhoto) {
      setError('Please upload a photo of your vehicle');
      return;
    }

    setLoading(true);

    try {
      // Convert capacity to kg if it's in tonnes
      const capacityInKg = getCapacityUnit() === 'tonnes' 
        ? parseFloat(formData.capacity_kg) * 1000 
        : parseFloat(formData.capacity_kg);

      // Create FormData for multipart upload
      const submitData = new FormData();
      submitData.append('email', userData.email);
      submitData.append('password', formData.password);
      submitData.append('first_name', userData.first_name);
      submitData.append('last_name', userData.last_name);
      submitData.append('phone', userData.phone);
      submitData.append('vehicle_type', formData.vehicle_type);
      submitData.append('license_plate', formData.license_plate || '');
      submitData.append('capacity_kg', capacityInKg.toString());
      submitData.append('make', formData.make);
      if (formData.model) submitData.append('model', formData.model);
      if (formData.year) submitData.append('year', formData.year);
      submitData.append('color', formData.color);
      submitData.append('bank_name', selectedBank?.name || '');
      submitData.append('bank_code', formData.bank_code);
      submitData.append('account_number', formData.account_number);
      submitData.append('account_name', formData.account_name);
      submitData.append('rider_photo', riderPhoto);
      submitData.append('vehicle_photo', vehiclePhoto);

      await api.post('/register/rider', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const errorMessages = errorDetail.map((e: any) => 
          `${e.loc?.join(' > ') || 'Error'}: ${e.msg}`
        ).join(', ');
        setError(errorMessages);
      } else if (typeof errorDetail === 'string') {
        setError(errorDetail);
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-white p-8 rounded-volexx-lg shadow-volexx-soft border border-gray-200 text-center">
        <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-gray-900 mb-2 font-poppins">Registration Successful!</h3>
        <p className="text-gray-600 mb-4 font-poppins">
          Your dispatcher application has been submitted for admin approval. You will be notified once approved.
        </p>
        <p className="text-sm text-gray-500 font-poppins">Redirecting to login...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-volexx-lg shadow-volexx-soft border border-gray-200">
      <h3 className="text-2xl font-bold text-gray-900 mb-2 font-poppins">Complete Your Registration</h3>
      <p className="text-gray-600 mb-6 font-poppins">Step 2: Provide vehicle and bank details</p>
      
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-volexx text-sm font-poppins">
            {error}
          </div>
        )}

        {/* Password Section */}
        <div className="border-b pb-5">
          <h4 className="text-lg font-bold text-gray-900 mb-4 font-poppins">Account Security</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="password"
                name="password"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                value={formData.password}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Vehicle Information */}
        <div className="border-b pb-5">
          <h4 className="text-lg font-bold text-gray-900 mb-4 font-poppins">Vehicle Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="vehicle_type" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                Vehicle Type <span className="text-red-500">*</span>
              </label>
              <select
                id="vehicle_type"
                name="vehicle_type"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                value={formData.vehicle_type}
                onChange={handleChange}
              >
                <option value="bicycle">Bicycle</option>
                <option value="motorbike">Motorbike</option>
                <option value="sedan">Sedan</option>
                <option value="suv">SUV</option>
                <option value="pickup_truck">Pickup Truck</option>
                <option value="medium_van">Medium Van</option>
                <option value="12_wheeler">12-Wheeler</option>
                <option value="18_wheeler">18-Wheeler</option>
              </select>
            </div>

            <div>
              <label htmlFor="license_plate" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                License Plate {isLicensePlateRequired() && <span className="text-red-500">*</span>}
                {!isLicensePlateRequired() && <span className="text-xs text-gray-500">(Not required for bicycles)</span>}
              </label>
              <input
                type="text"
                id="license_plate"
                name="license_plate"
                required={isLicensePlateRequired()}
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                value={formData.license_plate}
                onChange={handleChange}
                placeholder={isLicensePlateRequired() ? "Enter license plate" : "Optional"}
              />
            </div>

            <div>
              <label htmlFor="capacity_kg" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                Capacity ({getCapacityUnit()}) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                id="capacity_kg"
                name="capacity_kg"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                value={formData.capacity_kg}
                onChange={handleChange}
                placeholder={`Enter capacity in ${getCapacityUnit()}`}
              />
              {getCapacityUnit() === 'tonnes' && (
                <p className="text-xs text-gray-500 mt-1 font-poppins">Will be converted to kg for storage</p>
              )}
            </div>

            <div>
              <label htmlFor="make" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                Make <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="make"
                name="make"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                value={formData.make}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="model" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                Model
              </label>
              <input
                type="text"
                id="model"
                name="model"
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                value={formData.model}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="year" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                Year
              </label>
              <input
                type="number"
                id="year"
                name="year"
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                value={formData.year}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="color" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                Color <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="color"
                name="color"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                value={formData.color}
                onChange={handleChange}
              />
            </div>

            {/* Image Uploads - Full Width Section */}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {/* Your Photo Upload */}
            <div className="bg-gray-50 p-4 rounded-volexx border border-gray-200">
              <label className="block text-sm font-semibold text-gray-700 mb-3 font-poppins">
                Your Photo <span className="text-red-500">*</span>
              </label>
              
              {riderPhotoPreview ? (
                <div className="relative">
                  <img 
                    src={riderPhotoPreview} 
                    alt="Your photo preview" 
                    className="w-full h-48 object-cover rounded-volexx border-2 border-volexx-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => clearPhoto('rider')}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                  <p className="text-xs text-green-600 mt-2 font-poppins flex items-center">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    Photo uploaded successfully
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => riderCameraRef.current?.click()}
                      className="flex-1 flex items-center justify-center gap-2 bg-volexx-blue-500 hover:bg-volexx-blue-600 text-white py-3 px-4 rounded-volexx font-poppins font-semibold transition-colors"
                    >
                      <CameraIcon className="h-5 w-5" />
                      Take Photo
                    </button>
                    <button
                      type="button"
                      onClick={() => riderPhotoRef.current?.click()}
                      className="flex-1 flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-volexx font-poppins font-semibold transition-colors"
                    >
                      <PhotoIcon className="h-5 w-5" />
                      Upload
                    </button>
                  </div>
                  <input
                    ref={riderCameraRef}
                    type="file"
                    accept="image/*"
                    capture="user"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'rider')}
                  />
                  <input
                    ref={riderPhotoRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'rider')}
                  />
                  <p className="text-xs text-gray-500 font-poppins text-center">
                    Take or upload a clear photo of yourself
                  </p>
                </div>
              )}
            </div>

            {/* Vehicle Photo Upload */}
            <div className="bg-gray-50 p-4 rounded-volexx border border-gray-200">
              <label className="block text-sm font-semibold text-gray-700 mb-3 font-poppins">
                Vehicle Photo <span className="text-red-500">*</span>
              </label>
              
              {vehiclePhotoPreview ? (
                <div className="relative">
                  <img 
                    src={vehiclePhotoPreview} 
                    alt="Vehicle photo preview" 
                    className="w-full h-48 object-cover rounded-volexx border-2 border-volexx-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => clearPhoto('vehicle')}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                  <p className="text-xs text-green-600 mt-2 font-poppins flex items-center">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    Photo uploaded successfully
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => vehicleCameraRef.current?.click()}
                      className="flex-1 flex items-center justify-center gap-2 bg-volexx-blue-500 hover:bg-volexx-blue-600 text-white py-3 px-4 rounded-volexx font-poppins font-semibold transition-colors"
                    >
                      <CameraIcon className="h-5 w-5" />
                      Take Photo
                    </button>
                    <button
                      type="button"
                      onClick={() => vehiclePhotoRef.current?.click()}
                      className="flex-1 flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-volexx font-poppins font-semibold transition-colors"
                    >
                      <PhotoIcon className="h-5 w-5" />
                      Upload
                    </button>
                  </div>
                  <input
                    ref={vehicleCameraRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'vehicle')}
                  />
                  <input
                    ref={vehiclePhotoRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'vehicle')}
                  />
                  <p className="text-xs text-gray-500 font-poppins text-center">
                    Take or upload a clear photo of your {formData.vehicle_type}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bank Information */}
        <div>
          <h4 className="text-lg font-bold text-gray-900 mb-4 font-poppins">Bank Information</h4>
          <p className="text-red-600 text-sm font-semibold mb-4 font-poppins">
            ⚠️ Warning: Please ensure your bank information is accurate. Earnings will be paid to this account.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Bank Selection with Search */}
            <div className="relative" ref={bankDropdownRef}>
              <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                Select Bank <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder={banksLoading ? "Loading banks..." : "Search for your bank..."}
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                value={bankSearch}
                onChange={(e) => {
                  setBankSearch(e.target.value);
                  setShowBankDropdown(true);
                  if (selectedBank && e.target.value !== selectedBank.name) {
                    setSelectedBank(null);
                    setFormData({ ...formData, bank_code: '', account_name: '' });
                  }
                }}
                onFocus={() => setShowBankDropdown(true)}
                disabled={banksLoading}
              />
              {selectedBank && (
                <p className="text-xs text-green-600 mt-1 font-poppins flex items-center">
                  <CheckCircleIcon className="h-4 w-4 mr-1" />
                  {selectedBank.name} selected
                </p>
              )}
              
              {/* Bank Dropdown */}
              {showBankDropdown && !banksLoading && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-volexx shadow-lg max-h-60 overflow-y-auto">
                  {filteredBanks.length > 0 ? (
                    filteredBanks.slice(0, 20).map((bank) => (
                      <div
                        key={bank.code}
                        className="px-4 py-3 hover:bg-volexx-blue-50 cursor-pointer font-poppins text-sm border-b border-gray-100 last:border-b-0"
                        onClick={() => handleBankSelect(bank)}
                      >
                        {bank.name}
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-gray-500 font-poppins text-sm">
                      No banks found matching "{bankSearch}"
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Account Number */}
            <div>
              <label htmlFor="account_number" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                Account Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="account_number"
                placeholder="Enter 10-digit account number"
                maxLength={10}
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                value={formData.account_number}
                onChange={handleAccountNumberChange}
              />
              {verifyingAccount && (
                <p className="text-xs text-volexx-blue-500 mt-1 font-poppins flex items-center">
                  <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                  Verifying account...
                </p>
              )}
            </div>

            {/* Account Name */}
            <div className="md:col-span-2">
              <label htmlFor="account_name" className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                Account Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="account_name"
                name="account_name"
                placeholder={formData.account_name ? formData.account_name : "Will be auto-filled after verification"}
                className={`w-full px-4 py-3 border rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins ${
                  formData.account_name ? 'border-green-500 bg-green-50' : 'border-gray-300'
                }`}
                value={formData.account_name}
                onChange={handleChange}
                readOnly={!!formData.account_name && formData.account_name.length > 0}
              />
              {formData.account_name && (
                <p className="text-xs text-green-600 mt-1 font-poppins flex items-center">
                  <CheckCircleIcon className="h-4 w-4 mr-1" />
                  Account verified
                </p>
              )}
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-volexx-orange-500 hover:bg-volexx-orange-600 active:bg-volexx-orange-700 text-white font-bold py-3 px-6 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-orange-500 focus:ring-offset-2 font-poppins transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Completing Registration...' : 'Complete Registration'}
        </button>
      </form>
    </div>
  );
};

// Main Component: Two-Step Registration Flow
const DispatcherRegisterWithOTP: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [verifiedUserData, setVerifiedUserData] = useState<{
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  } | null>(null);

  const handleOTPVerified = (userData: { first_name: string; last_name: string; email: string; phone: string }) => {
    setVerifiedUserData(userData);
    setCurrentStep(2);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full">
        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            <div className={`flex items-center ${currentStep >= 1 ? 'text-volexx-blue-500' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold font-poppins ${currentStep >= 1 ? 'bg-volexx-blue-500 text-white' : 'bg-gray-300'}`}>
                1
              </div>
              <span className="ml-2 font-semibold font-poppins hidden sm:inline">Verify Identity</span>
            </div>
            <div className={`w-20 h-1 mx-4 ${currentStep >= 2 ? 'bg-volexx-blue-500' : 'bg-gray-300'}`}></div>
            <div className={`flex items-center ${currentStep >= 2 ? 'text-volexx-blue-500' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold font-poppins ${currentStep >= 2 ? 'bg-volexx-blue-500 text-white' : 'bg-gray-300'}`}>
                2
              </div>
              <span className="ml-2 font-semibold font-poppins hidden sm:inline">Complete Details</span>
            </div>
          </div>
        </div>

        {/* Step Content */}
        {currentStep === 1 && <Step1OTPVerification onVerified={handleOTPVerified} />}
        {currentStep === 2 && verifiedUserData && <Step2CompleteRegistration userData={verifiedUserData} />}
      </div>
    </div>
  );
};

export default DispatcherRegisterWithOTP;
